import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fornitoriApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Building2, User, Radio, CreditCard, Phone, Plus, Trash2, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';

// Lista province italiane
const PROVINCE_ITALIANE = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", "BR", "BS", "BT", "BZ",
  "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ", "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE",
  "GO", "GR", "IM", "IS", "KR", "LC", "LE", "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT",
  "NA", "NO", "NU", "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA", "RC",
  "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV", "TA", "TE", "TN", "TO", "TP",
  "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

const EmittenteForm = ({ emittente, onChange, onRemove, index }) => {
  const [showProvinceSelector, setShowProvinceSelector] = useState(false);
  const [provinciaSearch, setProvinciaSearch] = useState('');

  const handleSpotChange = (spotKey, field, value) => {
    const numValue = field === 'prezzo' ? parseFloat(value) || 0 : parseInt(value) || 0;
    onChange({
      ...emittente,
      [spotKey]: { ...emittente[spotKey], [field]: numValue }
    });
  };

  const handleAddProvincia = (provincia) => {
    const currentCopertura = emittente.copertura_provinciale || [];
    if (!currentCopertura.includes(provincia)) {
      onChange({
        ...emittente,
        copertura_provinciale: [...currentCopertura, provincia].sort()
      });
    }
    setProvinciaSearch('');
    setShowProvinceSelector(false);
  };

  const handleRemoveProvincia = (provincia) => {
    onChange({
      ...emittente,
      copertura_provinciale: (emittente.copertura_provinciale || []).filter(p => p !== provincia)
    });
  };

  const filteredProvince = PROVINCE_ITALIANE.filter(p => 
    p.toLowerCase().includes(provinciaSearch.toLowerCase()) &&
    !(emittente.copertura_provinciale || []).includes(p)
  );

  const spotTypes = [
    { key: 'spot_1min', label: '1 Minuto' },
    { key: 'spot_30sec', label: '30 Secondi' },
    { key: 'spot_20sec', label: '20 Secondi' },
    { key: 'spot_15sec', label: '15 Secondi' },
  ];

  return (
    <Card data-testid={`emittente-${index}-card`} className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 text-red-500 hover:text-red-700 hover:bg-red-50"
        onClick={onRemove}
        data-testid={`remove-emittente-${index}`}
      >
        <Trash2 size={18} />
      </Button>
      <CardHeader className="pb-4 pr-14">
        <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
          <Radio size={18} className="text-primary" />
          Emittente {index + 1}
        </CardTitle>
        <CardDescription>Configura i dati dell'emittente, la copertura provinciale e l'inventario spot</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Nome Emittente */}
        <div className="space-y-2">
          <Label htmlFor={`emittente${index}-nome`}>Nome Emittente</Label>
          <Input
            id={`emittente${index}-nome`}
            value={emittente.nome || ''}
            onChange={(e) => onChange({ ...emittente, nome: e.target.value })}
            placeholder="Es: Radio Deejay"
            data-testid={`emittente-${index}-nome`}
          />
        </div>

        <Separator />

        {/* Copertura Provinciale */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <MapPin size={16} className="text-primary" />
              Copertura Provinciale
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowProvinceSelector(!showProvinceSelector)}
              data-testid={`add-provincia-btn-${index}`}
            >
              <Plus size={14} className="mr-1" />
              Aggiungi Provincia
            </Button>
          </div>

          {/* Province selector */}
          {showProvinceSelector && (
            <div className="p-3 border rounded-lg bg-slate-50 space-y-2">
              <Input
                placeholder="Cerca provincia (es: RM, MI...)"
                value={provinciaSearch}
                onChange={(e) => setProvinciaSearch(e.target.value)}
                data-testid={`search-provincia-${index}`}
                className="h-9"
              />
              <div className="flex flex-wrap gap-1 max-h-32 overflow-auto">
                {filteredProvince.slice(0, 20).map((prov) => (
                  <Button
                    key={prov}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleAddProvincia(prov)}
                    data-testid={`provincia-option-${prov}`}
                  >
                    {prov}
                  </Button>
                ))}
                {filteredProvince.length > 20 && (
                  <span className="text-xs text-slate-500 py-1">...e altre {filteredProvince.length - 20}</span>
                )}
              </div>
            </div>
          )}

          {/* Province selezionate */}
          <div className="flex flex-wrap gap-2 min-h-[32px]">
            {(emittente.copertura_provinciale || []).length === 0 ? (
              <span className="text-sm text-slate-400 italic">Nessuna provincia selezionata</span>
            ) : (
              (emittente.copertura_provinciale || []).map((prov) => (
                <Badge key={prov} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                  {prov}
                  <button
                    type="button"
                    onClick={() => handleRemoveProvincia(prov)}
                    className="ml-1 hover:text-red-500"
                    data-testid={`remove-provincia-${prov}-${index}`}
                  >
                    <X size={12} />
                  </button>
                </Badge>
              ))
            )}
          </div>
          {(emittente.copertura_provinciale || []).length > 0 && (
            <p className="text-xs text-slate-500">
              Copertura su {emittente.copertura_provinciale.length} provinci{emittente.copertura_provinciale.length === 1 ? 'a' : 'e'}
            </p>
          )}
        </div>

        <Separator />

        {/* Inventario Spot */}
        <div>
          <h4 className="font-medium text-sm text-slate-700 mb-4">Inventario Spot Disponibili</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {spotTypes.map((spot) => (
              <div key={spot.key} className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h5 className="font-medium text-sm text-slate-900">{spot.label}</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Residuo (n° spot)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={emittente[spot.key]?.residuo || 0}
                      onChange={(e) => handleSpotChange(spot.key, 'residuo', e.target.value)}
                      data-testid={`emittente-${index}-${spot.key}-residuo`}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Prezzo unitario (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={emittente[spot.key]?.prezzo || 0}
                      onChange={(e) => handleSpotChange(spot.key, 'prezzo', e.target.value)}
                      data-testid={`emittente-${index}-${spot.key}-prezzo`}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const FornitoreDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fornitore, setFornitore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('anagrafica');

  useEffect(() => {
    const fetchFornitore = async () => {
      try {
        const response = await fornitoriApi.getOne(id);
        const data = response.data;
        
        // Migrazione: converti vecchio formato (emittente1/2/3) al nuovo (emittenti array)
        if (!data.emittenti) {
          const emittenti = [];
          if (data.emittente1?.nome) emittenti.push({ ...data.emittente1, copertura_provinciale: [] });
          if (data.emittente2?.nome) emittenti.push({ ...data.emittente2, copertura_provinciale: [] });
          if (data.emittente3?.nome) emittenti.push({ ...data.emittente3, copertura_provinciale: [] });
          data.emittenti = emittenti;
        }
        
        setFornitore(data);
      } catch (error) {
        toast.error('Errore nel caricamento');
        navigate('/fornitori');
      } finally {
        setLoading(false);
      }
    };
    fetchFornitore();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fornitoriApi.update(id, fornitore);
      toast.success('Fornitore salvato con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => setFornitore({ ...fornitore, [field]: value });

  const handleAddEmittente = () => {
    const newEmittente = {
      nome: '',
      copertura_provinciale: [],
      spot_1min: { residuo: 0, prezzo: 0 },
      spot_30sec: { residuo: 0, prezzo: 0 },
      spot_20sec: { residuo: 0, prezzo: 0 },
      spot_15sec: { residuo: 0, prezzo: 0 }
    };
    setFornitore({
      ...fornitore,
      emittenti: [...(fornitore.emittenti || []), newEmittente]
    });
  };

  const handleUpdateEmittente = (index, updatedEmittente) => {
    const newEmittenti = [...(fornitore.emittenti || [])];
    newEmittenti[index] = updatedEmittente;
    setFornitore({ ...fornitore, emittenti: newEmittenti });
  };

  const handleRemoveEmittente = (index) => {
    const newEmittenti = (fornitore.emittenti || []).filter((_, i) => i !== index);
    setFornitore({ ...fornitore, emittenti: newEmittenti });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!fornitore) return null;

  return (
    <div className="p-6 md:p-10" data-testid="fornitore-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/fornitori')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 tracking-tight">{fornitore.denominazione_sociale}</h1>
            <p className="text-slate-500 text-sm">{fornitore.comune}{fornitore.provincia ? `, ${fornitore.provincia}` : ''}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-fornitore-btn" className="bg-primary hover:bg-primary/90">
          <Save size={18} className="mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica"><Building2 size={16} className="mr-2" />Anagrafica</TabsTrigger>
          <TabsTrigger value="emittenti" data-testid="tab-emittenti">
            <Radio size={16} className="mr-2" />
            Emittenti
            {(fornitore.emittenti || []).length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">{fornitore.emittenti.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="anagrafica" className="space-y-6">
          <Card data-testid="dati-aziendali-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']"><Building2 size={20} className="text-primary" />Dati Aziendali</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="denominazione">Denominazione Sociale</Label>
                <Input id="denominazione" value={fornitore.denominazione_sociale} onChange={(e) => handleChange('denominazione_sociale', e.target.value)} data-testid="input-denominazione-sociale" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="indirizzo">Indirizzo</Label>
                <Input id="indirizzo" value={fornitore.indirizzo || ''} onChange={(e) => handleChange('indirizzo', e.target.value)} placeholder="Via Roma, 1" data-testid="input-indirizzo" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="comune">Comune</Label>
                  <Input id="comune" value={fornitore.comune || ''} onChange={(e) => handleChange('comune', e.target.value)} data-testid="input-comune" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provincia">Provincia</Label>
                  <Input id="provincia" value={fornitore.provincia || ''} onChange={(e) => handleChange('provincia', e.target.value)} data-testid="input-provincia" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="dati-fiscali-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']"><CreditCard size={20} className="text-primary" />Dati Fiscali</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                <Input id="codice_fiscale" value={fornitore.codice_fiscale || ''} onChange={(e) => handleChange('codice_fiscale', e.target.value)} data-testid="input-codice-fiscale" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partita_iva">Partita IVA</Label>
                <Input id="partita_iva" value={fornitore.partita_iva || ''} onChange={(e) => handleChange('partita_iva', e.target.value)} data-testid="input-partita-iva" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input id="iban" value={fornitore.iban || ''} onChange={(e) => handleChange('iban', e.target.value)} placeholder="IT60X0542811101000000123456" data-testid="input-iban" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="referenti-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']"><User size={20} className="text-primary" />Referenti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2"><Phone size={14} className="text-slate-500" />Referente 1</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ref1_nome">Nome</Label>
                    <Input id="ref1_nome" value={fornitore.referente1_nome || ''} onChange={(e) => handleChange('referente1_nome', e.target.value)} placeholder="Mario Rossi" data-testid="input-referente1-nome" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ref1_tel">Telefono</Label>
                    <Input id="ref1_tel" value={fornitore.referente1_telefono || ''} onChange={(e) => handleChange('referente1_telefono', e.target.value)} placeholder="+39 333 1234567" data-testid="input-referente1-telefono" />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2"><Phone size={14} className="text-slate-500" />Referente 2</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ref2_nome">Nome</Label>
                    <Input id="ref2_nome" value={fornitore.referente2_nome || ''} onChange={(e) => handleChange('referente2_nome', e.target.value)} placeholder="Luca Bianchi" data-testid="input-referente2-nome" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ref2_tel">Telefono</Label>
                    <Input id="ref2_tel" value={fornitore.referente2_telefono || ''} onChange={(e) => handleChange('referente2_telefono', e.target.value)} placeholder="+39 333 7654321" data-testid="input-referente2-telefono" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emittenti" className="space-y-6">
          {(fornitore.emittenti || []).length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Radio size={40} className="mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Nessuna emittente configurata</h3>
                <p className="text-slate-500 mb-4">Aggiungi le emittenti radio per questo fornitore</p>
                <Button onClick={handleAddEmittente} data-testid="add-first-emittente-btn">
                  <Plus size={18} className="mr-2" />
                  Aggiungi Emittente
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {fornitore.emittenti.map((emittente, index) => (
                <EmittenteForm
                  key={index}
                  emittente={emittente}
                  index={index}
                  onChange={(em) => handleUpdateEmittente(index, em)}
                  onRemove={() => handleRemoveEmittente(index)}
                />
              ))}
              <Button 
                onClick={handleAddEmittente} 
                variant="outline" 
                className="w-full border-dashed"
                data-testid="add-emittente-btn"
              >
                <Plus size={18} className="mr-2" />
                Aggiungi Emittente
              </Button>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FornitoreDetailPage;
